import React from "react";
import { Modal, Spin, Form, Input } from "antd";
import "./ExportModal.css";

function getDefaultTitle(exportType) {
  if (exportType === "xlsx") return "Export to Microsoft Excel";
  if (exportType === "ods") return "Export to OpenDocument (.ods)";
  if (exportType === "csv") return "Export to CSV";
  if (exportType === "html") return "Export to HTML";
  if (exportType === "grid") return "Export to MileGrid (.grid)";
  if (exportType === "pdf") return "Export to PDF";
  return "Export";
}

function getDefaultTip(exportType) {
  if (exportType === "xlsx") return "Generating .xlsx file...";
  if (exportType === "ods") return "Generating .ods file...";
  if (exportType === "csv") return "Generating .csv file...";
  if (exportType === "html") return "Generating .html file...";
  if (exportType === "grid") return "Preparing .grid export...";
  if (exportType === "pdf") return "Generating PDF file...";
  return "Preparing export...";
}

function getDefaultDescriptions(exportType) {
  if (exportType === "xlsx") {
    return [
      "Fully compatible with Microsoft Excel",
      "Preserves formatting, merges, freeze panes, and validations",
      "Charts are included as images for visual fidelity",
    ];
  }
  if (exportType === "grid") {
    return [
      "Native MileGrid .grid format with metadata",
      "Ideal for backups and collaboration",
    ];
  }

  if (exportType === "ods") {
    return [
      "OpenDocument Spreadsheet format (.ods)",
      "Recommended for LibreOffice and compatible editors",
    ];
  }

  if (exportType === "csv") {
    return [
      "Data-only export (no formatting, merges, or charts)",
      "Best for importing into other tools",
    ];
  }

  if (exportType === "html") {
    return [
      "Exports as an HTML table",
      "Useful for sharing or embedding in web pages",
    ];
  }

  if (exportType === "pdf") {
    return [
      "High-quality PDF with exact visual representation",
      "Perfect for sharing, printing, or archiving",
    ];
  }

  return ["Exports the current document", "Choose a safe file name"];
}

export default function ExportModal({
  visible,
  exportType,
  isExporting,
  fileName,
  form,
  onOk,
  onCancel,
  onFileNameChange,
}) {
  const title = getDefaultTitle(exportType);
  const okText = isExporting ? "Exporting..." : "Download";
  const tip = getDefaultTip(exportType);
  const descriptions = getDefaultDescriptions(exportType);

  return (
    <Modal
      title={title}
      visible={visible}
      onOk={onOk}
      onCancel={() => !isExporting && onCancel()}
      afterClose={() => form.resetFields()}
      okText={okText}
      cancelText="Cancel"
      confirmLoading={isExporting}
      okButtonProps={{ disabled: isExporting, className: "export-modal-btn-download" }}
      cancelButtonProps={{ disabled: isExporting, className: "export-modal-btn-cancel" }}
      closable={!isExporting}
      maskClosable={!isExporting}
      className="export-document-modal"
      centered
    >
      <Spin spinning={isExporting} tip={tip}>
        <Form form={form} layout="vertical">
          <Form.Item
            label="File Name"
            name="fileName"
            rules={[
              { required: true, message: "Please enter a file name." },
              {
                pattern: /^[^<>:;,?"*|/\\]+$/,
                message: "File name contains invalid characters.",
              },
            ]}
          >
            <Input
              value={fileName}
              onChange={onFileNameChange}
              placeholder="Enter file name"
              disabled={isExporting}
              className="export-modal-input"
            />
          </Form.Item>

          {exportType && (
            <div className="export-format-info">
              <div className="export-format-label">
                Export format: <strong>.{exportType}</strong>
              </div>

              {descriptions.map((text, idx) => (
                <div key={idx} className="export-format-description">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                  {text}
                </div>
              ))}
            </div>
          )}
        </Form>
      </Spin>
    </Modal>
  );
}
